import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { GoogleReCaptchaProvider, GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// '../../' is the path to src/
import config from '../../config';
import { FormInputComponent } from '../../Components/FormInputComponent';
import FormComponentController from './FormComponentController';

export function FormComponent( { title, handleSubmit, fields, navigateToOnComplete, navigateToOnCancel = '/', withReCaptcha, children } ) {

    const navigate = useNavigate();
    let inputStateObj = {};
    for( let thisField of fields ) {
        inputStateObj = {
            ...inputStateObj,
            [thisField.uniqueName]: ''
        };
    }

    let initializedStateObj = {};
    for( let thisField of fields ) {
        initializedStateObj = {
            ...initializedStateObj,
            [thisField.uniqueName]: false
        };
    }
    const [input, setInput] = useState( {...inputStateObj} );
    const [initialized] = useState({...initializedStateObj});
    const [error, setError] = useState( {} );
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [recaptchaReady, setRecaptchaReady] = useState(false);

    const controller = new FormComponentController( input, setInput, setLoading, error, setError, setErrorMessage, navigate, fields );

    const callHandleSubmit = (e) => {
        if (withReCaptcha && !recaptchaReady) {
            setErrorMessage('Please wait for reCAPTCHA to load before submitting.');
            e.preventDefault();
            return false;
        }

        if( withReCaptcha && !input.token ) {
            setErrorMessage('reCAPTCHA verification is required.');
            e.preventDefault();
            return false;
        }

        controller.handleSubmit(e, handleSubmit, navigateToOnComplete);
    }

    const onVerify = useCallback((token) => {
        setInput(prev => ({
            ...prev,
            token: token
        }));
        setRecaptchaReady(true);
    }, []);

    // Custom component to handle reCAPTCHA ready state
    const ReCaptchaWrapper = () => {
        const { loaded } = useGoogleReCaptcha();

        // Update ready state when reCAPTCHA is loaded
        if (loaded && !recaptchaReady) {
            setRecaptchaReady(true);
        }

        return <GoogleReCaptcha onVerify={onVerify} />;
    };

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 col-xl-6">
                    <div className="card shadow">
                        <div className="card-body">
                            <h2 className="text-center mb-4">{title}</h2>
            {errorMessage && (
                <p className="alert alert-danger" role="alert"> {errorMessage} </p>
            )}

            { children }
            <form name="form" onSubmit={callHandleSubmit}>
                {fields.map((field) => {
                    return (
                        <FormInputComponent
                            key={field.uniqueName}
                            input={input}
                            initialized={initialized}
                            error={error}
                            controller={controller}
                            uniqueName={field.uniqueName}
                            label={field.label}
                            inputType={field.attributeType}
                            initialValue={field.defaultValue}
                            fieldObject={field}
                        />
                    );
                })}

                { withReCaptcha && <>
                    <div className="mb-3">
                        <div id="recaptcha"></div>
                        {!recaptchaReady && (
                            <div className="text-center text-muted">
                                <small>Loading reCAPTCHA...</small>
                            </div>
                        )}
                    </div>
                    <GoogleReCaptchaProvider reCaptchaKey={config.recaptchaKey} useEnterprise="true"
                        container={ { element: 'recaptcha', parameters: { theme: 'dark' } } }
                    >
                        <ReCaptchaWrapper />
                    </GoogleReCaptchaProvider>
                  </>
                }

                            <div className="d-grid gap-2">
                                <button
                                    className="btn btn-primary"
                                    disabled={withReCaptcha && !recaptchaReady}
                                >
                                    {withReCaptcha && !recaptchaReady ? 'Loading...' : 'Submit'}
                                </button>
                            </div>
                            {loading &&
                                <div className="text-center mt-2">
                                    <img src="data:image/gif;base64,R0lGODlhEAAQAPIAAP///wAAAMLCwkJCQgAAAGJiYoKCgpKSkiH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCgAAACwAAAAAEAAQAAADMwi63P4wyklrE2MIOggZnAdOmGYJRbExwroUmcG2LmDEwnHQLVsYOd2mBzkYDAdKa+dIAAAh+QQJCgAAACwAAAAAEAAQAAADNAi63P5OjCEgG4QMu7DmikRxQlFUYDEZIGBMRVsaqHwctXXf7WEYB4Ag1xjihkMZsiUkKhIAIfkECQoAAAAsAAAAABAAEAAAAzYIujIjK8pByJDMlFYvBoVjHA70GU7xSUJhmKtwHPAKzLO9HMaoKwJZ7Rf8AYPDDzKpZBqfvwQAIfkECQoAAAAsAAAAABAAEAAAAzMIumIlK8oyhpHsnFZfhYumCYUhDAQxRIdhHBGqRoKw0R8DYlJd8z0fMDgsGo/IpHI5TAAAIfkECQoAAAAsAAAAABAAEAAAAzIIunInK0rnZBTwGPNMgQwmdsNgXGJUlIWEuR5oWUIpz8pAEAMe6TwfwyYsGo/IpFKSAAAh+QQJCgAAACwAAAAAEAAQAAADMwi6IMKQORfjdOe82p4wGccc4CEuQradylesojEMBgsUc2G7sDX3lQGBMLAJibufbSlKAAAh+QQJCgAAACwAAAAAEAAQAAADMgi63P7wCRHZnFVdmgHu2nFwlWCI3WGc3TSWhUFGxTAUkGCbtgENBMJAEJsxgMLWzpEAACH5BAkKAAAALAAAAAAQABAAAAMyCLrc/jDKSatlQtScKdceCAjDII7HcQ4EMTCpyrCuUBjCYRgHVtqlAiB1YhiCnlsRkAAAOwAAAAAAAAAAAA==" alt="loading"/>
                                </div>
                            }
                            <div className="text-center mt-3">
                                <Link to={navigateToOnCancel} className="btn btn-link">Cancel</Link>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      );
}